import React, { useEffect, useMemo, useState } from 'react';
import api from '../api';

// Стили — копия из pages/PlanFact.js (controlStyle, choiceStyle, rowButtonStyle, Field).
// Держать одинаковыми: блок обязан выглядеть как форма «Разноска».
const fmt = (value) => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  return Math.round(value).toLocaleString('ru-RU');
};

const controlStyle = {
  height: 40,
  boxSizing: 'border-box',
  background: 'var(--bg)',
  color: 'var(--text1)',
  border: '1px solid var(--border)',
  borderRadius: 8,
  padding: '0 12px',
  fontSize: 13,
  fontWeight: 600,
  width: '100%',
};

const rowButtonStyle = {
  height: 32,
  minWidth: 88,
  borderRadius: 8,
  border: '1px solid var(--border)',
  background: '#fff',
  cursor: 'pointer',
  fontWeight: 700,
  whiteSpace: 'nowrap',
};

const primaryStyle = {
  height: 40, minWidth: 140, padding: '0 16px', border: 'none', borderRadius: 8,
  background: 'var(--accent)', color: '#fff', fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap',
};

const cellStyle = { padding: '8px 10px', borderBottom: '1px solid var(--border)' };
const headStyle = {
  textAlign: 'left', padding: '8px 10px', color: 'var(--text2)', fontSize: 11,
  borderBottom: '1px solid var(--border)', whiteSpace: 'nowrap',
};

function choiceStyle(active) {
  return {
    width: '100%', height: 40, padding: '0 8px', borderRadius: 8, cursor: 'pointer',
    fontWeight: 700, fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
    border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
    background: active ? 'var(--accent)' : '#fff',
    color: active ? '#fff' : 'var(--text1)',
  };
}

function Field({ label, children, style }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0, ...style }}>
      <span style={{ fontSize: 12, color: 'var(--text2)', height: 16, whiteSpace: 'nowrap' }}>{label || ' '}</span>
      {children}
    </label>
  );
}

function Tile({ title, value, hint }) {
  return (
    <div style={{ background: 'var(--bg3)', borderRadius: 10, padding: '10px 12px', minWidth: 0 }}>
      <div style={{ fontWeight: 800, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{title}</div>
      <div style={{ fontSize: 18, fontWeight: 800, marginTop: 4, whiteSpace: 'nowrap' }}>{fmt(value)}</div>
      {hint && <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

const CASH_QUICK = [
  ['Зарплатный', 'Аванс / ФОТ'],
  ['Покупка товаров', 'Товар'],
  ['Дивы', 'Дивиденды'],
];

function localToday() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
}

const parseAmount = (value) => parseFloat(String(value).replace(/\s/g, '').replace(',', '.')) || 0;

export default function CashSplit({ period, units, entryArticles, quickArticles, onSaved, onPeriod }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [mode, setMode] = useState('withdraw');
  const [form, setForm] = useState({ move_date: localToday(), amount: '', note: '', statement: 'both', bank_txn_id: null, bank_note: '' });
  const [lines, setLines] = useState([]);
  const [line, setLine] = useState({ unit_codes: ['retail'], article_code: '', amount: '' });
  const [saving, setSaving] = useState(false);

  const load = () => {
    api.get('/finance/cash', { params: { period } })
      .then((response) => setData(response.data))
      .catch((err) => setError(err?.response?.data?.detail || 'Не удалось открыть наличные'));
  };
  useEffect(load, [period]); // eslint-disable-line react-hooks/exhaustive-deps

  const articles = useMemo(() => {
    const list = entryArticles || [];
    const own = CASH_QUICK.map(([needle, label]) => {
      const article = list.find((item) => item.name.toLowerCase().includes(needle.toLowerCase()));
      return article ? { ...article, label } : null;
    }).filter(Boolean);
    const seen = new Set(own.map((item) => item.code));
    return [...own, ...(quickArticles || []).filter((item) => !seen.has(item.code))];
  }, [entryArticles, quickArticles]);

  useEffect(() => {
    if (!line.article_code && articles.length) setLine((prev) => ({ ...prev, article_code: articles[0].code }));
  }, [articles, line.article_code]);

  const projectUnits = (units || []).filter((item) => item.code !== 'company');
  const total = parseAmount(form.amount);
  const allocated = lines.reduce((sum, item) => sum + item.amount, 0);
  const balance = data?.balance || 0;
  const rest = mode === 'withdraw' ? total - allocated : balance - allocated;
  const articleName = (code) => (entryArticles || []).find((item) => item.code === code)?.name || code;
  const unitName = (code) => projectUnits.find((item) => item.code === code)?.name || code;

  const addLine = () => {
    const amount = parseAmount(line.amount);
    setError('');
    if (!line.unit_codes.length) { setError('Выберите проект для строки'); return; }
    if (!line.article_code) { setError('Выберите статью'); return; }
    if (!amount) { setError('Укажите сумму строки'); return; }
    if (rest - amount < -0.004) {
      setError(mode === 'withdraw' ? 'Строки больше суммы снятия. Остаток не может быть отрицательным' : `В сейфе только ${fmt(balance)}`);
      return;
    }
    setLines((prev) => [...prev, { key: Date.now(), unit_codes: [...line.unit_codes], article_code: line.article_code, amount }]);
    setLine((prev) => ({ ...prev, amount: '' }));
  };

  const pickBank = (row) => {
    setMode('withdraw');
    setForm((prev) => ({ ...prev, move_date: row.move_date, amount: String(row.amount), bank_txn_id: row.bank_txn_id, bank_note: row.note }));
    if (onPeriod && row.move_date) onPeriod(row.move_date.slice(0, 7));
  };

  const reset = () => {
    setLines([]);
    setForm((prev) => ({ ...prev, amount: '', note: '', bank_txn_id: null, bank_note: '' }));
  };

  const save = async () => {
    setError('');
    const payload = lines.map((item) => ({ unit_codes: item.unit_codes, article_code: item.article_code, amount: item.amount }));
    if (mode === 'withdraw' && !form.bank_txn_id && !total) { setError('Укажите сумму снятия'); return; }
    if (mode === 'safe' && !payload.length) { setError('Добавьте хотя бы одну строку'); return; }
    setSaving(true);
    try {
      if (mode === 'withdraw') {
        await api.post('/finance/cash/withdraw', {
          move_date: form.move_date, amount: total, bank_txn_id: form.bank_txn_id,
          note: form.note, statement: form.statement, lines: payload,
        });
      } else {
        await api.post('/finance/cash/spend', { move_date: form.move_date, note: form.note, statement: form.statement, lines: payload });
      }
      reset();
      load();
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не сохранилось');
    } finally {
      setSaving(false);
    }
  };

  const removeWithdrawal = async (row) => {
    if (!window.confirm('Удалить снятие вместе со строками разбивки?')) return;
    setError('');
    try {
      await api.delete(`/finance/cash/withdraw/${row.id}`);
      load();
      if (onSaved) onSaved();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Не удалилось');
    }
  };

  const month = data?.month || {};
  const bank = data?.bank || [];
  const withdrawals = data?.withdrawals || [];

  return (
    <div style={{ marginTop: 22, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 10 }}>
        <div style={{ fontWeight: 800 }}>Наличные</div>
        <div style={{ fontSize: 12, color: 'var(--text2)' }}>{data?.note || 'Снятие — перевод банк → сейф, не расход. В ДДС попадают только строки разбивки.'}</div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginBottom: 14 }}>
        <Tile title="В сейфе" value={balance} hint="остаток наличных сейчас" />
        <Tile title="Снято за месяц" value={month.withdrawn} hint="банк → сейф" />
        <Tile title="Разнесено из снятий" value={month.allocated} hint="строки в ДДС" />
        <Tile title="Из сейфа за месяц" value={month.safe_spent} hint="траты наличными" />
        <Tile title="ФОТ наличными" value={month.payroll_cash} hint="уменьшает кассу ведомости" />
      </div>

      {error && <div style={{ color: 'var(--red)', fontSize: 13, marginBottom: 12 }}>{error}</div>}

      {!!bank.length && (
        <>
          <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>Снятия в банке, которые ещё не разбиты по проектам</div>
          <div style={{ overflow: 'auto', marginBottom: 14 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <tbody>
                {bank.map((row) => (
                  <tr key={row.bank_txn_id}>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{row.move_date}</td>
                    <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(row.amount)}</td>
                    <td style={{ ...cellStyle, color: 'var(--text2)' }}>{row.note}</td>
                    <td style={cellStyle}>
                      <button type="button" onClick={() => pickBank(row)} style={{
                        ...rowButtonStyle,
                        border: `1px solid ${form.bank_txn_id === row.bank_txn_id ? 'var(--accent)' : 'var(--border)'}`,
                        background: form.bank_txn_id === row.bank_txn_id ? 'var(--accent)' : '#fff',
                        color: form.bank_txn_id === row.bank_txn_id ? '#fff' : 'var(--text1)',
                      }}>Разбить</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
        <Field label="Операция" style={{ width: 320 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { id: 'withdraw', label: 'Снятие с банка' },
              { id: 'safe', label: 'Трата из сейфа' },
            ].map((item) => (
              <button key={item.id} type="button" onClick={() => { setMode(item.id); setLines([]); setForm((prev) => ({ ...prev, bank_txn_id: null, bank_note: '', amount: '' })); }} style={{
                ...choiceStyle(mode === item.id), flex: '1 1 0', width: 'auto',
              }}>{item.label}</button>
            ))}
          </div>
        </Field>
        <Field label="Дата" style={{ width: 160 }}>
          <input type="date" value={form.move_date} disabled={!!form.bank_txn_id} onChange={(event) => {
            const move_date = event.target.value;
            setForm((prev) => ({ ...prev, move_date }));
            if (onPeriod && move_date) onPeriod(move_date.slice(0, 7));
          }} style={controlStyle} />
        </Field>
        {mode === 'withdraw' && (
          <Field label="Сумма снятия" style={{ width: 150 }}>
            <input type="number" value={form.amount} placeholder="0" disabled={!!form.bank_txn_id} onChange={(event) => setForm((prev) => ({ ...prev, amount: event.target.value }))} style={controlStyle} />
          </Field>
        )}
        <Field label="Куда" style={{ width: 240 }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {[
              { id: 'dds', label: 'ДДС' },
              { id: 'pnl', label: 'P&L' },
              { id: 'both', label: 'Оба' },
            ].map((item) => (
              <button key={item.id} type="button" onClick={() => setForm((prev) => ({ ...prev, statement: item.id }))} style={{
                ...choiceStyle(form.statement === item.id), flex: '1 1 0', width: 'auto',
              }}>{item.label}</button>
            ))}
          </div>
        </Field>
        <Field label="Комментарий" style={{ flex: '1 1 180px' }}>
          <input value={form.note} placeholder={form.bank_note || 'кто снял, на что'} onChange={(event) => setForm((prev) => ({ ...prev, note: event.target.value }))} style={controlStyle} />
        </Field>
      </div>
      {form.bank_txn_id && (
        <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: -6, marginBottom: 12 }}>
          Из банка: {form.bank_note}. Сумма и дата взяты из операции.
        </div>
      )}

      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>Строка разбивки: проект. Можно отметить несколько — сумма разделится поровну.</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 14 }}>
        {projectUnits.map((item) => (
          <button key={item.code} type="button" title={item.full} onClick={() => setLine((prev) => ({
            ...prev,
            unit_codes: prev.unit_codes.includes(item.code) ? prev.unit_codes.filter((code) => code !== item.code) : [...prev.unit_codes, item.code],
          }))} style={choiceStyle(line.unit_codes.includes(item.code))}>{item.name}</button>
        ))}
      </div>
      <div style={{ fontSize: 12, color: 'var(--text2)', marginBottom: 6 }}>Статья</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8, marginBottom: 14 }}>
        {articles.map((item) => (
          <button key={item.code} type="button" title={item.name} onClick={() => setLine((prev) => ({ ...prev, article_code: item.code }))} style={choiceStyle(line.article_code === item.code)}>{item.label}</button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <Field label="Другая статья" style={{ flex: '1 1 220px' }}>
          <select value={line.article_code} onChange={(event) => setLine((prev) => ({ ...prev, article_code: event.target.value }))} style={controlStyle}>
            {['income', 'expense', 'below', 'result'].map((group) => {
              const options = (entryArticles || []).filter((item) => item.group === group);
              if (!options.length) return null;
              const title = { income: 'Доходы', expense: 'Расходы', below: 'Ниже прибыли', result: 'Результат' }[group];
              return (
                <optgroup key={group} label={title}>
                  {options.map((item) => <option key={item.code} value={item.code}>{item.name}</option>)}
                </optgroup>
              );
            })}
          </select>
        </Field>
        <Field label="Сумма" style={{ width: 150 }}>
          <input type="number" value={line.amount} placeholder="0" onChange={(event) => setLine((prev) => ({ ...prev, amount: event.target.value }))} onKeyDown={(event) => { if (event.key === 'Enter') addLine(); }} style={controlStyle} />
        </Field>
        <Field label=" ">
          <button type="button" onClick={addLine} style={primaryStyle}>Добавить строку</button>
        </Field>
      </div>

      {!!lines.length && (
        <div style={{ overflow: 'auto', marginTop: 12 }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr>
                {['Проект', 'Статья', 'Сумма', ''].map((label) => <th key={label} style={headStyle}>{label}</th>)}
              </tr>
            </thead>
            <tbody>
              {lines.map((item) => (
                <tr key={item.key}>
                  <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{item.unit_codes.map(unitName).join(', ')}</td>
                  <td style={cellStyle}>{articleName(item.article_code)}</td>
                  <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(item.amount)}</td>
                  <td style={cellStyle}>
                    <button type="button" onClick={() => setLines((prev) => prev.filter((row) => row.key !== item.key))} style={rowButtonStyle}>Удалить</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginTop: 12 }}>
        <div style={{ fontSize: 13, color: 'var(--text2)', flex: 1, minWidth: 200 }}>
          {mode === 'withdraw'
            ? <>Строк на {fmt(allocated)}. <b style={{ color: rest < 0 ? 'var(--red)' : 'var(--text1)' }}>Остаток в сейф {fmt(rest)}</b></>
            : <>Строк на {fmt(allocated)}. <b style={{ color: rest < 0 ? 'var(--red)' : 'var(--text1)' }}>В сейфе останется {fmt(rest)}</b></>}
        </div>
        <button type="button" disabled={saving} onClick={save} style={primaryStyle}>
          {saving ? '…' : (mode === 'withdraw' ? 'Сохранить снятие' : 'Списать из сейфа')}
        </button>
      </div>

      {!!withdrawals.length && (
        <>
          <div style={{ marginTop: 18, fontSize: 13, color: 'var(--text2)' }}>Снятия за {period}: {withdrawals.length}</div>
          <div style={{ overflow: 'auto', marginTop: 8 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr>
                  {['Дата', 'Снято', 'Разнесено', 'В сейф', 'Строки', 'Комментарий', ''].map((label) => <th key={label} style={headStyle}>{label}</th>)}
                </tr>
              </thead>
              <tbody>
                {withdrawals.map((row) => (
                  <tr key={row.id}>
                    <td style={{ ...cellStyle, whiteSpace: 'nowrap' }}>{row.move_date}</td>
                    <td style={{ ...cellStyle, textAlign: 'right', fontWeight: 700, whiteSpace: 'nowrap' }}>{fmt(row.amount)}</td>
                    <td style={{ ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(row.allocated)}</td>
                    <td style={{ ...cellStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>{fmt(row.rest)}</td>
                    <td style={{ ...cellStyle, color: 'var(--text2)' }}>
                      {row.lines.map((item) => `${item.unit_name} · ${item.article_name} ${fmt(item.amount)}`).join('; ') || '—'}
                    </td>
                    <td style={{ ...cellStyle, color: 'var(--text2)' }}>{row.bank_txn_id ? 'банк · ' : ''}{row.note}</td>
                    <td style={cellStyle}>
                      <button type="button" onClick={() => removeWithdrawal(row)} style={rowButtonStyle}>Удалить</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
